import { useState, useMemo } from "react";

/**
 * Hook reutilizable para ordenar listas por columna.
 * Uso: const { sorted, SortIcon, handleSort, sortConfig } = useSort(data, "name");
 */
export function useSort(data = [], defaultKey = "", defaultDir = "asc") {
  const [sortConfig, setSortConfig] = useState({ key: defaultKey, dir: defaultDir });

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc",
    }));
  };

  const sorted = useMemo(() => {
    if (!sortConfig.key) return data;
    return [...data].sort((a, b) => {
      const aVal = a[sortConfig.key] ?? "";
      const bVal = b[sortConfig.key] ?? "";
      const aStr = typeof aVal === "string" ? aVal.toLowerCase() : aVal;
      const bStr = typeof bVal === "string" ? bVal.toLowerCase() : bVal;
      if (aStr < bStr) return sortConfig.dir === "asc" ? -1 : 1;
      if (aStr > bStr) return sortConfig.dir === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig]);

  // Renders a sort indicator for a column header
  const SortIcon = ({ col }) => {
    if (sortConfig.key !== col) return <span className="text-slate-300 ml-1">↕</span>;
    return <span className="text-brand-600 ml-1">{sortConfig.dir === "asc" ? "↑" : "↓"}</span>;
  };

  return { sorted, SortIcon, handleSort, sortConfig };
}

/**
 * Hook reutilizable para selección masiva con checkboxes.
 * Uso: const { selected, toggleOne, toggleAll, clearAll, isAllSelected } = useBulkSelect(data);
 */
export function useBulkSelect(data = []) {
  const [selected, setSelected] = useState(new Set());

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === data.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(data.map((d) => d.id)));
    }
  };

  const clearAll = () => setSelected(new Set());

  const isAllSelected = data.length > 0 && selected.size === data.length;
  const isSomeSelected = selected.size > 0 && selected.size < data.length;

  return { selected, toggleOne, toggleAll, clearAll, isAllSelected, isSomeSelected };
}
